import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Chip,
  Paper,
  Stack,
  CircularProgress,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getCollaborationWorkspace,
  createCollaborationWorkspace,
  updateCollaborationWorkspace,
  searchTenantUsersAdmin
} from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';

const CollaborationWorkspaceEditorPage = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const isNew = workspaceId === 'new';
  const { userData, tenantId: authTenantId } = useAuth();
  const tenantId = authTenantId || userData?.tenantId;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    participants: [],
    googleMeetUrl: '',
    zoomMeetingUrl: '',
    googleCalendarUrl: '',
    googleSlidesUrl: '',
    googleDriveUrl: '',
    notes: ''
  });

  // Participant picker state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [customEntry, setCustomEntry] = useState('');

  // Load existing workspace
  useEffect(() => {
    if (isNew || !workspaceId || !tenantId) { setLoading(false); return; }
    const load = async () => {
      try {
        const ws = await getCollaborationWorkspace(tenantId, workspaceId);
        if (!ws) { setError('Workspace not found'); setLoading(false); return; }
        setFormData({
          name: ws.name || '',
          description: ws.description || '',
          participants: ws.participants || [],
          googleMeetUrl: ws.googleMeetUrl || '',
          zoomMeetingUrl: ws.zoomMeetingUrl || '',
          googleCalendarUrl: ws.googleCalendarUrl || '',
          googleSlidesUrl: ws.googleSlidesUrl || '',
          googleDriveUrl: ws.googleDriveUrl || '',
          notes: ws.notes || ''
        });
      } catch (err) {
        setError(err.message || 'Failed to load workspace');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, isNew, tenantId]);

  // Debounced user search
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) { setSearchResults([]); setSearchLoading(false); return; }
    let active = true;
    setSearchLoading(true);
    const id = setTimeout(async () => {
      try {
        const results = await searchTenantUsersAdmin(tenantId, term, 25);
        if (active) setSearchResults(Array.isArray(results) ? results : []);
      } catch (_) {
        if (active) setSearchResults([]);
      } finally {
        if (active) setSearchLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(id); };
  }, [searchTerm, tenantId]);

  const addParticipant = (name) => {
    if (!name || formData.participants.includes(name)) return;
    setFormData((prev) => ({ ...prev, participants: [...prev.participants, name] }));
  };

  const removeParticipant = (index) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    if (!formData.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const resp = await createCollaborationWorkspace(tenantId, formData);
        setSuccess('Workspace created');
        const newId = resp?.workspace?.id;
        if (newId) setTimeout(() => navigate(`/admin/collaboration-workspaces/${newId}`), 1000);
      } else {
        await updateCollaborationWorkspace(tenantId, workspaceId, formData);
        setSuccess('Workspace saved');
      }
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {isNew ? 'Create Collaboration Workspace' : 'Edit Collaboration Workspace'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/collaboration-workspaces')}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* Name & Description */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Workspace Info</Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Workspace Name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                required
                helperText="A clear name for this collaboration workspace"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                helperText="Short purpose description (optional)"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Google Links */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Meeting & Collaboration Links</Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Google Meet URL"
                value={formData.googleMeetUrl}
                onChange={(e) => setFormData((p) => ({ ...p, googleMeetUrl: e.target.value }))}
                placeholder="https://meet.google.com/..."
                helperText="Opens in a new tab"
              />
              <TextField
                fullWidth
                label="Zoom Meeting URL"
                value={formData.zoomMeetingUrl}
                onChange={(e) => setFormData((p) => ({ ...p, zoomMeetingUrl: e.target.value }))}
                placeholder="https://zoom.us/j/..."
                helperText="Opens in a new tab"
              />
              <TextField
                fullWidth
                label="Google Calendar URL"
                value={formData.googleCalendarUrl}
                onChange={(e) => setFormData((p) => ({ ...p, googleCalendarUrl: e.target.value }))}
                placeholder="https://calendar.google.com/..."
                helperText="Embeds if it uses /calendar/embed format, otherwise opens in new tab"
              />
              <TextField
                fullWidth
                label="Google Slides URL"
                value={formData.googleSlidesUrl}
                onChange={(e) => setFormData((p) => ({ ...p, googleSlidesUrl: e.target.value }))}
                placeholder="https://docs.google.com/presentation/..."
                helperText="Embeds if it uses /embed or /pub format"
              />
              <TextField
                fullWidth
                label="Google Drive URL"
                value={formData.googleDriveUrl}
                onChange={(e) => setFormData((p) => ({ ...p, googleDriveUrl: e.target.value }))}
                placeholder="https://drive.google.com/..."
                helperText="Opens in a new tab"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Participants */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Participants</Typography>
              <Tooltip title="Search users by name/email or add custom names. Multiple participants supported.">
                <InfoOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            {/* Current participants */}
            {formData.participants.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {formData.participants.map((p, i) => (
                  <Chip key={i} label={p} size="small" onDelete={() => removeParticipant(i)} />
                ))}
              </Box>
            )}

            {/* User search */}
            <Box sx={{ position: 'relative', mb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search users by name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  helperText="Type 2+ characters to search"
                />
                {searchLoading && <CircularProgress size={20} sx={{ flexShrink: 0 }} />}
              </Stack>

              {searchResults.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{ position: 'absolute', top: 'calc(100% - 20px)', left: 0, right: 0, zIndex: 20, maxHeight: 200, overflow: 'auto' }}
                >
                  {searchResults.map((u) => {
                    const name = u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id;
                    const value = (u.email && name !== u.email) ? `${name} (${u.email})` : (u.email || name);
                    const alreadyAdded = formData.participants.includes(value);
                    return (
                      <Box
                        key={u.id || u.email}
                        onClick={() => {
                          if (alreadyAdded) return;
                          addParticipant(value);
                          setSearchTerm('');
                          setSearchResults([]);
                        }}
                        sx={{
                          px: 2, py: 1,
                          cursor: alreadyAdded ? 'default' : 'pointer',
                          opacity: alreadyAdded ? 0.5 : 1,
                          '&:hover': { bgcolor: alreadyAdded ? 'transparent' : 'action.hover' }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                        {u.email && name !== u.email && (
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        )}
                      </Box>
                    );
                  })}
                </Paper>
              )}
            </Box>

            {/* Custom entry */}
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Add custom name or role"
                value={customEntry}
                onChange={(e) => setCustomEntry(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const entry = customEntry.trim();
                    if (entry) { addParticipant(entry); setCustomEntry(''); }
                  }
                }}
                placeholder="e.g. Security Team"
                helperText="Press Enter or click Add"
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!customEntry.trim()}
                onClick={() => {
                  const entry = customEntry.trim();
                  if (entry) { addParticipant(entry); setCustomEntry(''); }
                }}
                sx={{ alignSelf: 'flex-start', mt: 0.25, flexShrink: 0 }}
              >
                Add
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              helperText="Internal notes displayed in the workspace"
            />
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default CollaborationWorkspaceEditorPage;
